/**
 * useFeatureFlag Hook
 *
 * React hook for accessing PostHog feature flags
 * Automatically updates when flags are loaded/changed
 */

import { useState, useEffect } from 'react';
import { isFeatureEnabled, getFeatureFlag, posthog } from '../utils/analytics';

/**
 * Hook to check if a feature flag is enabled
 * @param {string} flag - Feature flag name
 * @param {boolean} defaultValue - Default value before flags load
 * @returns {boolean} Whether the flag is enabled
 */
export function useFeatureFlag(flag, defaultValue = false) {
  const [enabled, setEnabled] = useState(defaultValue);

  useEffect(() => {
    // Only override default if flags are already loaded
    if (posthog && posthog.featureFlags?.getFlagVariants) {
      const variants = posthog.featureFlags.getFlagVariants();
      if (Object.keys(variants).length > 0) {
        setEnabled(isFeatureEnabled(flag));
      }
    }

    // Update when flags load from server
    if (posthog && typeof posthog.onFeatureFlags === 'function') {
      posthog.onFeatureFlags(() => {
        setEnabled(isFeatureEnabled(flag));
      });
    }
  }, [flag]);

  return enabled;
}

/**
 * Hook to get a multivariate feature flag value
 * @param {string} flag - Feature flag name
 * @param {any} defaultValue - Default value before flags load
 * @returns {string|boolean|null} The flag value
 */
export function useFeatureFlagValue(flag, defaultValue = null) {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    // Set initial value
    setValue(getFeatureFlag(flag) ?? defaultValue);

    // Listen for feature flags to load
    if (posthog && typeof posthog.onFeatureFlags === 'function') {
      posthog.onFeatureFlags(() => {
        setValue(getFeatureFlag(flag) ?? defaultValue);
      });
    }
  }, [flag, defaultValue]);

  return value;
}

/**
 * Hook to get all feature flags
 * @returns {Object} Object containing all feature flags
 */
export function useFeatureFlags() {
  const [flags, setFlags] = useState({});

  useEffect(() => {
    const updateFlags = () => {
      if (posthog && typeof posthog.getFeatureFlag === 'function') {
        // Get all flags - PostHog stores them internally
        const allFlags = posthog.featureFlags?.getFlagVariants?.() || {};
        setFlags(allFlags);
      }
    };

    updateFlags();

    if (posthog && typeof posthog.onFeatureFlags === 'function') {
      posthog.onFeatureFlags(updateFlags);
    }
  }, []);

  return flags;
}

export default useFeatureFlag;
